import { useCallback } from 'react'
import { useNavigate, useParams, useMatch } from 'react-router-dom-v5-compat'

import {
  calendarMonth,
  calendarWeek,
  newCalendarAllChannels,
  newCalendarSingleChannel,
} from '~publish/legacy/routes'

/**
 * Represents the view mode of the calendar.
 * Can be either 'week' or 'month'.
 */
export type CalendarDateRangeViewMode = 'week' | 'month'

/**
 * A custom hook that manages the view mode of the calendar.
 * It handles the routing logic for switching between week and month views,
 * and supports both legacy and new calendar routes.
 *
 * @returns {[CalendarDateRangeViewMode, (mode: CalendarDateRangeViewMode | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode)) => void]}
 * A tuple containing:
 *   - The current view mode ('week' or 'month')
 *   - A function to set the view mode
 */
export const useCalendarDateRangeViewMode = (): [
  CalendarDateRangeViewMode,
  (
    mode:
      | CalendarDateRangeViewMode
      | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode),
  ) => void,
] => {
  const navigate = useNavigate()

  const isLegacyCalendarWeek = !!useMatch(calendarWeek.route)
  const isLegacyCalendarMonth = !!useMatch(calendarMonth.route)
  const isLegacyView = isLegacyCalendarWeek || isLegacyCalendarMonth
  const isLegacyWeekView = isLegacyCalendarWeek
  const isSingleChannel = !!useMatch(newCalendarSingleChannel.route)

  const params = useParams<{
    granularity: CalendarDateRangeViewMode
    id: string
  }>()

  const viewMode =
    isLegacyWeekView || params.granularity === 'week' ? 'week' : 'month'

  /**
   * Navigates to the specified calendar view mode.
   *
   * @param {CalendarDateRangeViewMode} mode - The view mode to navigate to ('week' or 'month')
   */
  const goTo = useCallback(
    (mode: 'week' | 'month') => {
      let route: string
      if (isLegacyView) {
        if (mode === 'week') {
          route = calendarWeek.getRoute()
        } else {
          route = calendarMonth.getRoute()
        }
      } else {
        if (isSingleChannel) {
          route = newCalendarSingleChannel.getRoute(params.id ?? '', mode)
        } else {
          route = newCalendarAllChannels.getRoute(mode)
        }
      }
      navigate(route)
    },
    [navigate, isLegacyView, isSingleChannel, params.id],
  )

  /**
   * Sets the view mode of the calendar.
   * It can accept either a ViewMode directly, or a function that receives the current ViewMode
   * and returns a new ViewMode.
   *
   * @param {CalendarDateRangeViewMode | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode)} mode - The new view mode or a function to compute it
   */
  const setViewMode = useCallback(
    (
      mode:
        | CalendarDateRangeViewMode
        | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode),
    ): void => {
      if (typeof mode === 'function') {
        const newViewMode = mode(viewMode)
        goTo(newViewMode)
      } else {
        goTo(mode)
      }
    },
    [goTo, viewMode],
  )

  return [viewMode, setViewMode]
}
